import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import { SocialMediaConfiguration } from './SocialMediaConfiguration';
import { SocialMediaCalendar } from './SocialMediaCalendar';
import { SocialMediaPostManager } from './SocialMediaPostManager';
import { DiscountCodeBank } from './DiscountCodeBank';
import { OfferDistribution } from './OfferDistribution';
import { GoogleBusiness } from './GoogleBusiness';
import { LinkedInBusiness } from './LinkedInBusiness';
import { ReviewManagement } from './ReviewManagement';
import { ReviewInbox } from '../recovery/ReviewInbox';
import { EscalationBanner } from '../recovery/EscalationBanner';
import './SocialMediaDashboard.css';

interface DashboardStats {
  totalPosts: number;
  scheduledPosts: number;
  activeCampaigns: number;
  totalImpressions: number;
  totalEngagements: number;
  totalClicks: number;
  activeDiscountCodes: number;
  codeRedemptions: number;
  connectedAccounts: number;
  revenue: number;
}

type TabId = 'overview' | 'accounts' | 'calendar' | 'posts' | 'codes' | 'offers' | 'google' | 'linkedin' | 'reviews' | 'recovery';

interface TabDef {
  id: TabId;
  label: string;
  group: 'main' | 'platforms' | 'reputation';
}

const TABS: TabDef[] = [
  { id: 'overview', label: 'Overview', group: 'main' },
  { id: 'accounts', label: 'Accounts', group: 'main' },
  { id: 'posts', label: 'Posts', group: 'main' },
  { id: 'calendar', label: 'Calendar', group: 'main' },
  { id: 'codes', label: 'Codes', group: 'main' },
  { id: 'offers', label: 'Offers', group: 'main' },
  { id: 'google', label: 'Google', group: 'platforms' },
  { id: 'linkedin', label: 'LinkedIn', group: 'platforms' },
  { id: 'reviews', label: 'Reviews', group: 'reputation' },
  { id: 'recovery', label: 'Recovery', group: 'reputation' },
];

export const SocialMediaDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [stats, setStats] = useState<DashboardStats>({
    totalPosts: 0, scheduledPosts: 0, activeCampaigns: 0,
    totalImpressions: 0, totalEngagements: 0, totalClicks: 0,
    activeDiscountCodes: 0, codeRedemptions: 0, connectedAccounts: 0, revenue: 0
  });
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (selectedStore) fetchDashboardData(); }, [selectedStore]);

  const fetchInitialData = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.stores());
      if (response.ok) {
        const data = await response.json();
        const storeList = Array.isArray(data) ? data : (data.data || data.stores || []);
        setStores(storeList);
        if (storeList.length > 0) {
          setSelectedStore(storeList[0].storeId);
        } else {
          setError('No stores available for social media management');
        }
      } else {
        throw new Error(`Failed to fetch stores: ${response.status}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stores');
    }
  };

  const fetchDashboardData = async () => {
    if (!selectedStore) return;
    setLoading(true);
    try {
      const [postsData, campaignsData, codesData, accountsData] = await Promise.all([
        authenticatedFetch(`${API_ENDPOINTS.socialMediaPosts()}?storeId=${selectedStore}`)
          .then(r => r.ok ? r.json() : { data: [] }),
        authenticatedFetch(`${API_ENDPOINTS.socialMediaCampaigns()}?storeId=${selectedStore}`)
          .then(r => r.ok ? r.json() : { data: [] }),
        authenticatedFetch(`${API_ENDPOINTS.discountCodes()}?storeId=${selectedStore}`)
          .then(r => r.ok ? r.json() : { data: [] }),
        authenticatedFetch(`${API_ENDPOINTS.socialMediaAccounts()}?storeId=${selectedStore}`)
          .then(r => r.ok ? r.json() : { data: [] }),
      ]);

      const posts = postsData.data || [];
      const campaigns = campaignsData.data || [];
      const codes = codesData.data || [];
      const accounts = accountsData.data || [];

      const totalImpressions = posts.reduce((sum: number, post: any) => {
        return sum + Object.values(post.impressions || {}).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
      }, 0);
      const totalEngagements = posts.reduce((sum: number, post: any) => {
        return sum + Object.values(post.engagements || {}).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
      }, 0);
      const totalClicks = posts.reduce((sum: number, post: any) => {
        return sum + Object.values(post.clicks || {}).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
      }, 0);
      const totalRevenue = posts.reduce((sum: number, post: any) => sum + (post.revenue || 0), 0);
      const activeDiscountCodes = codes.filter((code: any) => code.isActive && new Date(code.validUntil) > new Date()).length;
      const codeRedemptions = codes.reduce((sum: number, code: any) => sum + (code.currentUses || 0), 0);

      setStats({
        totalPosts: posts.length,
        scheduledPosts: posts.filter((post: any) => post.status === 'scheduled').length,
        activeCampaigns: campaigns.filter((c: any) => c.status === 'active').length,
        totalImpressions, totalEngagements, totalClicks,
        activeDiscountCodes, codeRedemptions,
        connectedAccounts: accounts.filter((acc: any) => acc.isActive).length,
        revenue: totalRevenue
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const selectedStoreInfo = stores.find(s => s.storeId === selectedStore);
  const storeName = selectedStoreInfo?.name || 'Unknown Store';

  const renderOverview = () => (
    <div className="sm-overview">
      <div className="sm-stats-row">
        <div className="sm-stat-card">
          <div className="sm-stat-number">{stats.totalPosts}</div>
          <div className="sm-stat-label">Total Posts</div>
          <div className="sm-stat-detail">{stats.scheduledPosts} scheduled</div>
        </div>
        <div className="sm-stat-card">
          <div className="sm-stat-number">{formatNumber(stats.totalImpressions)}</div>
          <div className="sm-stat-label">Impressions</div>
          <div className="sm-stat-detail">All platforms</div>
        </div>
        <div className="sm-stat-card">
          <div className="sm-stat-number">{formatNumber(stats.totalEngagements)}</div>
          <div className="sm-stat-label">Engagements</div>
          <div className="sm-stat-detail">Likes, comments, shares</div>
        </div>
        <div className="sm-stat-card">
          <div className="sm-stat-number">{formatNumber(stats.totalClicks)}</div>
          <div className="sm-stat-label">Clicks</div>
          <div className="sm-stat-detail">Link clicks</div>
        </div>
      </div>

      <div className="sm-stats-row sm-stats-row-secondary">
        <div className="sm-stat-card sm-stat-compact">
          <div className="sm-stat-number">{stats.activeCampaigns}</div>
          <div className="sm-stat-label">Active Campaigns</div>
        </div>
        <div className="sm-stat-card sm-stat-compact">
          <div className="sm-stat-number">{stats.activeDiscountCodes}</div>
          <div className="sm-stat-label">Active Codes</div>
        </div>
        <div className="sm-stat-card sm-stat-compact">
          <div className="sm-stat-number">{stats.connectedAccounts}</div>
          <div className="sm-stat-label">Connected Accounts</div>
        </div>
        <div className="sm-stat-card sm-stat-compact">
          <div className="sm-stat-number">{formatCurrency(stats.revenue)}</div>
          <div className="sm-stat-label">Revenue</div>
        </div>
      </div>

      <div className="sm-overview-panels">
        <div className="sm-panel">
          <h3 className="sm-panel-title">Quick Actions</h3>
          <div className="sm-quick-actions">
            <button className="sm-action-btn" onClick={() => setActiveTab('posts')}>
              <span className="sm-action-icon">+</span>
              <span>Create Post</span>
            </button>
            <button className="sm-action-btn" onClick={() => setActiveTab('offers')}>
              <span className="sm-action-icon">%</span>
              <span>Distribute Offer</span>
            </button>
            <button className="sm-action-btn" onClick={() => setActiveTab('codes')}>
              <span className="sm-action-icon">#</span>
              <span>Generate Codes</span>
            </button>
            <button className="sm-action-btn" onClick={() => setActiveTab('calendar')}>
              <span className="sm-action-icon">Cal</span>
              <span>Schedule Posts</span>
            </button>
          </div>
        </div>

        <div className="sm-panel">
          <h3 className="sm-panel-title">Connected Platforms</h3>
          <div className="sm-platform-status">
            <button className="sm-platform-row" onClick={() => setActiveTab('accounts')}>
              <span className="sm-platform-name">Facebook</span>
              <span className={`sm-platform-badge ${stats.connectedAccounts > 0 ? 'connected' : ''}`}>
                {stats.connectedAccounts > 0 ? 'Connected' : 'Not Connected'}
              </span>
            </button>
            <button className="sm-platform-row" onClick={() => setActiveTab('linkedin')}>
              <span className="sm-platform-name">LinkedIn</span>
              <span className="sm-platform-badge connected">Connected</span>
            </button>
            <button className="sm-platform-row" onClick={() => setActiveTab('google')}>
              <span className="sm-platform-name">Google Business</span>
              <span className="sm-platform-badge">Setup</span>
            </button>
            <button className="sm-platform-row" onClick={() => setActiveTab('reviews')}>
              <span className="sm-platform-name">Reviews</span>
              <span className="sm-platform-badge">Manage</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'accounts': return <SocialMediaConfiguration storeId={selectedStore || ''} storeName={storeName} />;
      case 'calendar': return <SocialMediaCalendar />;
      case 'posts': return <SocialMediaPostManager storeId={selectedStore || ''} />;
      case 'codes': return <DiscountCodeBank />;
      case 'offers': return <OfferDistribution storeId={selectedStore} />;
      case 'google': return <GoogleBusiness storeId={selectedStore || ''} storeName={storeName} />;
      case 'linkedin': return <LinkedInBusiness storeId={selectedStore || ''} storeName={storeName} />;
      case 'reviews': return <ReviewManagement storeId={selectedStore || ''} storeName={storeName} />;
      case 'recovery': return <ReviewInbox storeId={selectedStore || ''} storeName={storeName} />;
      default: return renderOverview();
    }
  };

  if (loading && !selectedStore) {
    return (
      <div className="sm-loading">
        <div className="sm-loading-spinner" />
        <p>Loading Social Media Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="sm-dashboard">
      {selectedStore && (
        <EscalationBanner
          storeId={selectedStore}
          onNavigateToRecovery={() => setActiveTab('recovery')}
        />
      )}

      <header className="sm-header">
        <div className="sm-header-left">
          <h1 className="sm-title">Social Media</h1>
          <p className="sm-subtitle">Manage your social presence across all platforms</p>
        </div>
        {stores.length > 0 && (
          <div className="sm-header-right">
            <label className="sm-store-label" htmlFor="sm-store-select">Store</label>
            <select
              id="sm-store-select"
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="sm-store-select"
            >
              {stores.map(store => (
                <option key={store.storeId} value={store.storeId}>
                  {store.name}{store.address?.city ? ` - ${store.address.city}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </header>

      {error && (
        <div className="sm-error-bar">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="sm-error-dismiss">Dismiss</button>
        </div>
      )}

      <nav className="sm-nav">
        <div className="sm-nav-group">
          {TABS.filter(t => t.group === 'main').map(tab => (
            <button
              key={tab.id}
              className={`sm-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="sm-nav-divider" />
        <div className="sm-nav-group">
          {TABS.filter(t => t.group === 'platforms').map(tab => (
            <button
              key={tab.id}
              className={`sm-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="sm-nav-divider" />
        <div className="sm-nav-group">
          {TABS.filter(t => t.group === 'reputation').map(tab => (
            <button
              key={tab.id}
              className={`sm-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="sm-content">
        {renderTabContent()}
      </main>
    </div>
  );
};
